import React, { useState, useCallback } from 'react';
import MySquare from '../../basic/MySquare';
import MySquareIcon from '../../basic/MySquareIcon';
import MyProgressBar from '../../basic/MyProgressBar';
import MyDropdownButton from '../../basic/MyDropdownButton';
import ModalAccountTransactions from './modalAccountTransactions';
import ModalTransactions from './modalTransactions';
import MyConfirmationModal from '../../basic/MyConfirmationModal.js';
import ModalEditAccount from './ModalEditAccount';
import ModalBalanceSheet from './ModalBalanceSheet';
import { deleteChildAccount } from '../../../api';
import { HelpCircle, Goal, Scale } from 'lucide-react';
import './AccountCard.css';
import { useFamily } from '../../../context/FamilyContext';
import { currencies } from '../../basic/currencies';

function AccountCard({ account, onTransactionSuccess, onDeleteSuccess, onEditSuccess, isCompactView }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isTransactionHistoryModalOpen, setIsTransactionHistoryModalOpen] = useState(false);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isBalanceSheetModalOpen, setIsBalanceSheetModalOpen] = useState(false);
    const [transactionType, setTransactionType] = useState('');
    const { currencyCode } = useFamily();

    const { allowance_amount, goal_amount, account_balance, user_id, account_id, account_name, is_default, splitDetails } = account;

    const getCurrencySymbol = useCallback(() => {
        const currency = currencies.find(c => c.code === currencyCode);
        return currency ? currency.symbol : '$'; // Default to '$' if not found
    }, [currencyCode]);

    const calculateSplitAmount = useCallback((percentage) => {
        return ((allowance_amount * percentage) / 100).toFixed(2);
    }, [allowance_amount]);

    const goalProgress = goal_amount ? (account_balance / goal_amount) * 100 : 0;

    const handleOpenModal = useCallback((type) => {
        setTransactionType(type);
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => setIsModalOpen(false), []);
    const handleOpenTransactionHistoryModal = useCallback(() => setIsTransactionHistoryModalOpen(true), []);
    const handleCloseTransactionHistoryModal = useCallback(() => setIsTransactionHistoryModalOpen(false), []);
    
    const handleOpenBalanceSheetModal = useCallback(() => setIsBalanceSheetModalOpen(true), []);
    const handleCloseBalanceSheetModal = useCallback(() => setIsBalanceSheetModalOpen(false), []);

    const handleTransactionSuccess = useCallback((message) => {
        onTransactionSuccess(message);
        handleCloseModal();
    }, [onTransactionSuccess, handleCloseModal]);

    const handleDeleteAccount = useCallback(async () => {
        try {
            await deleteChildAccount(user_id, account_id);
            onDeleteSuccess(`Account "${account_name}" has been deleted successfully.`);
        } catch (error) {
            console.error('Error deleting account:', error);
        }
        setIsDeleteConfirmationOpen(false);
    }, [user_id, account_id, account_name, onDeleteSuccess]);

    const handleOpenEditModal = useCallback(() => setIsEditModalOpen(true), []);
    const handleCloseEditModal = useCallback(() => setIsEditModalOpen(false), []);

    const handleEditSuccess = useCallback((message) => {
        onEditSuccess(message);
        handleCloseEditModal();
    }, [onEditSuccess, handleCloseEditModal]);

    const dropdownOptions = [
        ...(is_default ? [] : [
            { label: 'Edit Account', action: handleOpenEditModal },
            { label: 'Delete Account', action: () => setIsDeleteConfirmationOpen(true) }
        ]),
        { label: 'Show Transactions', action: handleOpenTransactionHistoryModal },
    ];

    const tooltipText = `
        <p><strong>Income splits settings</strong> determine how deposits such as allowances will be distributed between the accounts.</p>
        <p>For example, with the current allowance amount, this account will receive <strong>${getCurrencySymbol()}${calculateSplitAmount(splitDetails[0]?.percentage || 0)}</strong> as it is configured for a <strong>${splitDetails[0]?.percentage || 0}%</strong> split.</p>
    `;
    
    return (
        <div 
            className={`account-card ${is_default ? 'default-account' : ''} ${isCompactView ? 'compact-view' : ''}`}
            data-account-type={account_name === "Checking" ? "checking" : "other"}
        >
            <div className="account-details">
                <h3 className="account-name">
                    {account_name}
                    {goal_amount > 0 && (
                        <Goal size={20} className="goal-icon" />
                    )}
                </h3>
                <p> <strong>{getCurrencySymbol()}{account_balance}</strong></p>                
            </div>
            {!isCompactView && (
                <>
                    <div className={`goal-details ${!goal_amount ? 'empty' : ''}`}>
                        {goal_amount && (
                            <>
                                <h4>Saving Goal</h4>
                                <p>{getCurrencySymbol()}{goal_amount}</p>
                                <MyProgressBar 
                                    value={account_balance} 
                                    max={goal_amount} 
                                    label={`${goalProgress.toFixed(0)}%`}
                                    splitAmount={calculateSplitAmount(splitDetails[0]?.percentage || 0)} 
                                    />
                            </>
                        )}
                    </div>
                    <div className="split-details">
                        <h4>
                            Income Split
                            <span className="tooltip-container">
                                <HelpCircle size={16} className="tooltip-icon" />
                                <span className="tooltip-text" dangerouslySetInnerHTML={{ __html: tooltipText }}></span>
                            </span>
                        </h4>
                        {splitDetails.length > 0 ? (
                            <>
                                <div className="income-splits-circle">
                                    <div 
                                        className="pie-chart" 
                                        style={{ 
                                            background: `conic-gradient(#3498db 0deg, #3498db ${splitDetails[0].percentage * 3.6}deg, #eee ${splitDetails[0].percentage * 3.6}deg)` 
                                        }}
                                    >
                                        <span className="pie-chart-text">{splitDetails[0].percentage}%</span>
                                    </div>
                                </div>
                                <p className="split-percentage">{splitDetails[0].percentage}%</p>
                            </>
                        ) : <p>No splits defined</p>}
                    </div>
                    <div className="account-actions">
                        <div className="button-grid">
                            <MySquare className="action-button deposit-button" color="#838d63" symbol="+" onClick={() => handleOpenModal('deposit')} />
                            <MySquare className="action-button withdraw-button" color="#b16863" symbol="-" onClick={() => handleOpenModal('withdrawal')} />
                            <MyDropdownButton className="options-button" options={dropdownOptions} />
                            {account_name === "Checking" && (
                                <MySquareIcon 
                                    className="action-button balance-sheet-button" 
                                    color="#808080" 
                                    icon={Scale} 
                                    onClick={handleOpenBalanceSheetModal} 
                                />
                            )}
                        </div>
                    </div>
                </>
            )}
            <ModalAccountTransactions
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                childId={user_id}
                accountId={account_id}
                accountName={account_name}
                balance={account_balance}
                transactionType={transactionType}
                onTransactionSuccess={handleTransactionSuccess}
            />
            <ModalTransactions
                isOpen={isTransactionHistoryModalOpen}
                onRequestClose={handleCloseTransactionHistoryModal}
                userId={user_id}
                accountId={account_id}
                accountName={account_name}
            />
            <MyConfirmationModal
                isOpen={isDeleteConfirmationOpen}
                onClose={() => setIsDeleteConfirmationOpen(false)}
                onConfirm={handleDeleteAccount}
                title="Delete Account"
                message={`NOTE - If you delete this account, ${getCurrencySymbol()}${account_balance} will be transferred to the Checking account - Are you sure you want to delete the account "${account_name}"? This action cannot be undone.`}
            />
            <ModalEditAccount
                isOpen={isEditModalOpen}
                onClose={handleCloseEditModal}
                childId={user_id}
                accountId={account_id}
                onEditSuccess={handleEditSuccess}
            />
            <ModalBalanceSheet
                isOpen={isBalanceSheetModalOpen}
                onClose={handleCloseBalanceSheetModal}
                childId={user_id}
                accountId={account_id}
                accountName={account_name}
            />
        </div>
    );
}

export default AccountCard;